import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiPlay, FiSettings, FiSearch, FiShield, FiKey, FiActivity, FiArrowRight, FiCheckCircle, FiLoader, FiAlertCircle } from 'react-icons/fi';

const steps = [
  { id: 1, title: 'Docking', desc: 'Securely connect your data sources (Databases, S3, APIs) to the harbor.', icon: FiSettings },
  { id: 2, title: 'Beacon Sync', desc: 'Automated crawlers extract metadata, schemas, and statistics.', icon: FiLoader },
  { id: 3, title: 'Discovery', desc: 'Users search the catalog to find relevant datasets for their projects.', icon: FiSearch },
  { id: 4, title: 'Lineage Trace', desc: 'Trace the origin and destination of every column automatically.', icon: FiActivity },
  { id: 5, title: 'Access Bay', desc: 'Streamlined approval workflow for sensitive data boarding.', icon: FiKey },
  { id: 6, title: 'Quality Sonar', desc: 'Continuous monitoring for data drift and schema changes.', icon: FiShield },
  { id: 7, title: 'Outcome', desc: 'Teams make confident, data-driven decisions with trusted assets.', icon: FiCheckCircle }
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(1);
  const [simulatedState, setSimulatedState] = useState('idle'); // idle, loading, success

  const handleSimulate = () => {
    setSimulatedState('loading');
    setTimeout(() => {
      setSimulatedState('success');
      setTimeout(() => setSimulatedState('idle'), 3000);
    }, 2000);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--pearl)] mb-1">Demo / How It Works</h2>
        <p className="text-[var(--pearl-muted)] text-sm">Experience the end-to-end DataHarbor journey.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Step List */}
        <div className="lg:col-span-4 space-y-2">
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              className={`w-full text-left p-4 rounded-lg border transition-all flex items-center gap-4 ${
                activeStep === step.id 
                ? 'bg-[var(--schema-teal-dim)] border-[var(--schema-teal)] shadow-[0_0_15px_var(--schema-teal-dim)]' 
                : 'bg-[var(--surface-ocean)] border-[var(--border-ocean)] hover:border-[var(--pearl-muted)]'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                activeStep === step.id ? 'bg-[var(--schema-teal)] text-[var(--bg-ocean)]' : 'bg-[var(--surface-ocean-light)] text-[var(--pearl-muted)]'
              }`}>
                {step.id}
              </div>
              <div>
                <p className={`font-semibold text-sm ${activeStep === step.id ? 'text-[var(--schema-teal)]' : 'text-[var(--pearl)]'}`}>{step.title}</p>
                <p className="text-[10px] text-[var(--pearl-muted)] line-clamp-1">{step.desc}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Details & Simulation */}
        <div className="lg:col-span-8 space-y-6">
          <div className="glass-panel p-8 min-h-[400px] flex flex-col justify-between">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-2xl bg-[var(--surface-ocean-light)] border border-[var(--border-ocean)] shadow-lg">
                    <SafeIcon icon={steps[activeStep-1].icon} className="text-4xl text-[var(--schema-teal)]" />
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-[var(--pearl)]">Stage {activeStep}: {steps[activeStep-1].title}</h3>
                    <p className="text-[var(--schema-teal)] text-sm tracking-widest uppercase">Workflow Protocol</p>
                  </div>
                </div>

                <p className="text-[var(--pearl-muted)] text-lg leading-relaxed">
                  {steps[activeStep-1].desc}
                </p>

                <div className="grid grid-cols-2 gap-4 mt-8">
                  <div className="p-4 rounded-lg bg-[var(--bg-ocean)] border border-[var(--border-ocean)]">
                    <p className="text-[10px] text-[var(--schema-teal)] uppercase mb-1">Key Outcome</p>
                    <p className="text-sm">Verified Metadata Integrity</p>
                  </div>
                  <div className="p-4 rounded-lg bg-[var(--bg-ocean)] border border-[var(--border-ocean)]">
                    <p className="text-[10px] text-[var(--schema-teal)] uppercase mb-1">Primary Actor</p>
                    <p className="text-sm">Data Architect / Engineer</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-12 flex flex-col items-center">
              <div className="w-full h-1 bg-[var(--surface-ocean-light)] mb-8 overflow-hidden rounded-full">
                <motion.div 
                  className="h-full bg-[var(--schema-teal)] shadow-[0_0_10px_var(--schema-teal)]"
                  initial={{ width: '0%' }}
                  animate={{ width: `${(activeStep / steps.length) * 100}%` }}
                />
              </div>

              <div className="flex items-center gap-4 w-full">
                <button 
                  onClick={handleSimulate}
                  disabled={simulatedState === 'loading'}
                  className="btn-primary flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {simulatedState === 'loading' ? (
                    <><SafeIcon icon={FiLoader} className="animate-spin" /> Processing Sector...</>
                  ) : simulatedState === 'success' ? (
                    <><SafeIcon icon={FiCheckCircle} /> Simulation Complete</>
                  ) : (
                    <><SafeIcon icon={FiPlay} /> Try this flow</>
                  )}
                </button>
                {activeStep < steps.length && (
                  <button 
                    onClick={() => setActiveStep(prev => prev + 1)}
                    className="btn-secondary py-3 flex items-center gap-2"
                  >
                    Next Stage <SafeIcon icon={FiArrowRight} />
                  </button>
                )}
              </div>

              <div className="mt-4 flex gap-4 w-full">
                {simulatedState === 'loading' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 text-center text-xs text-[var(--schema-teal)] animate-pulse">
                    Requesting harbor clearance...
                  </motion.div>
                )}
                {simulatedState === 'success' && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex-1 text-center text-xs text-[var(--success-mint)] flex items-center justify-center gap-2">
                    <SafeIcon icon={FiCheckCircle} /> State updated in harbor ledger.
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}